import { z } from 'zod';

// Shared Feature Toggle Schema
const FeatureToggleSchema = z.record(
  z.object({
    enabled: z.boolean(),
    disabledMessage: z
      .object({
        title: z.string(),
        subtitle: z.string(),
        content: z.string(),
      })
      .optional(),
  }),
);

// Shared Outage Window Schema
const OutageWindowSchema = z
  .object({
    irregular: z.array(
      z.object({
        start: z.union([z.string(), z.number()]),
        end: z.union([z.string(), z.number()]),
      }),
    ),
    regular: z
      .object({
        baseTimestamp: z.number(),
        duration: z.number(),
        interval: z.number(),
      })
      .optional(),
  })
  .strict();

// Base Rules Schema for shared rules
const BaseRulesSchema = z
  .object({
    MAX_TRIP_CALENDAR_MONTHS: z.number(),
    MIN_DATE_OFFSET_DAYS: z.number(),
    IDLE_EMAIL_QUOTE_REMINDER_SECONDS: z.number(),
    MAX_TRIP_DAYS: z.number(),
    PARTNER_ID_VALIDITY_DURATION_IN_MINUTES: z.number(),
    MAX_DESTINATIONS: z.number(),
    PROTECTION_RESTRICTION_DAYS: z.number(),
    COOL_OFF_PERIOD_DAYS: z.number(),
    PAYMENT_TIMEOUT_AFTER_MINUTES: z.number(),
    PRIMARY_TRAVELLER_AGE: z.object({ min: z.number(), max: z.number() }),
    MAX_TRIP_CALENDAR_DAYS: z.number(),
    MAX_SPECIFIED_ITEM_IN_BOOST: z.number(),
    MAX_START_DATE_OFFSET: z.number(),
    PAYMENT_TIMEOUT_ENABLED: z.boolean(),
    SECONDARY_TRAVELLER_MAX_AGE: z.number(),
    RESIDENT_CHECKBOX: z.boolean(),
    PAYMENT_PROCESSING_TIMEOUT_SECONDS: z.number(),
    IDLE_SESSION_TIMEOUT_MINUTES: z.number(),
    MAX_SECONDARY_TRAVELLERS: z.number(),
    SECONDARY_TRAVELLER_AGE: z.object({
      min: z.number(),
      max: z.number(),
      mid: z.object({ min: z.number(), max: z.number() }).optional(),
    }),
    CAN_UPDATE_POLICY: z.boolean(),
  })
  .strict();

// AU-specific Rules Schema
const AURulesSchema = BaseRulesSchema.extend({
  AFTER_PAY_MAX_AMOUNT_LIMIT: z.number().optional(),
  AFTER_PAY_TRIP_END_DATE_OFFSET: z.number().optional(),
  MAX_TRIP_DAYS_EMC: z.number().optional(),
  TRIP_PRE_DEPARTURE_CANCELLATION_SAFE_DAYS: z.number().optional(),
}).strict();

// US-specific Rules Schema
const USRulesSchema = BaseRulesSchema.extend({
  TRIP_COST_MIN_PER_PERSON: z.number(),
  TRIP_COST_MAX_PER_PERSON: z.number(),
  TRIP_COST_MED_PER_PERSON: z.number(),
  TRIP_COST_BY_DEPARTURE_DAYS: z.number(),
  PRE_CANCELLATION_BENEFITS: z.object({
    MAX_DEPARTURE_DAYS_OFFSET_FROM_TODAY: z.number(),
  }),
  EXCEPTIONS: z.record(
    z.object({
      COOL_OFF_PERIOD_DAYS: z.number().optional(),
      MAX_TRIP_DAYS: z.number().optional(),
    }),
  ),
}).strict();

// Base Region Schema for shared properties
const BaseRegionSchema = z
  .object({
    APP_TERMS: z.string(),
    CODE: z.string(),
    CURRENCY: z.string(),
    CURRENCY_SYMBOL: z.string(),
    CUSTOMER_SERVICE_EMAIL: z.string(),
    CUSTOMER_SERVICE_EMERGENCY_NUMBER: z.string(),
    CUSTOMER_SERVICE_NUMBER: z.string(),
    CUSTOMER_SERVICE_OVERSEAS_NUMBER: z.string(),
    DEFAULT_PRODUCT_ID: z.string(),
    FEATURE_TOGGLE: FeatureToggleSchema,
    FREELY_OUTAGE_WINDOW: OutageWindowSchema,
    IS_MOCK: z.boolean(),
    LOCALE: z.string(),
    MARKETING_IMAGE_TAGS: z.record(z.string()),
    NAME: z.string(),
    OUTAGE_WINDOW: OutageWindowSchema,
    PDS: z.object({ CURRENT: z.string() }),
    PRIVACY_STATEMENT: z.string(),
    SUPPORT_ENQUIRY_GROUPS: z
      .array(
        z.array(
          z.object({
            label: z.string(),
            layout: z.string().optional(),
            url: z.string().optional(),
            value: z.string(),
            size: z.string().optional(),
          }),
        ),
      )
      .optional(),
    TARGET_MARKET: z.string().optional(),
    TRAVEL_ALERTS: z.string().optional(),
    VALIDATION: z.object({
      postcodeRegex: z.string(),
      postCodeRegexMessage: z.string(),
    }),
    FRAUD_WARNING: z.string().optional(),
  })
  .strict();

// AU-specific Schema
const AUSchema = BaseRegionSchema.merge(
  z.object({
    RULES: AURulesSchema,
    FREELY_OUTAGE_WINDOW_DESCRIPTION: z.string().optional(),
    FREELY_OUTAGE_WINDOW_TERMS_AND_CONDITIONS: z.string().optional(),
  }),
).strict();

// US-specific Schema
const USSchema = BaseRegionSchema.merge(
  z.object({
    RULES: USRulesSchema,
    NATIVE_PAYMENTS: z
      .object({
        APPLE_PAY: z.boolean(),
        GOOGLE_PAY: z.boolean(),
      })
      .optional(),
  }),
).strict();

// Complete WebConfigSchema
export const WebConfigSchema = z
  .object({
    APPSTORE: z.object({
      rating: z.number(),
      reviewCount: z.number(),
    }),
    APPS_FLYER_DEEP_LINK: z.string(),
    APP_DOWNLOAD_LINK: z.string(),
    APP_STORE_LINK: z.string(),
    AU: AUSchema,
    US: USSchema,
    ECOMM_URL: z.string(),
    PLAY_STORE_LINK: z.string(),
    S3_ASSETS_BUCKET: z.string(),
    SELECT_REGION: z.object({
      default: z.string(),
      enabled: z.boolean(),
    }),
    STRIPE_API_VERSION: z.string(),
  })
  .strict();
